import type { CarFleetViewRecord, UserGroupAddressRecord } from '@navio/nsdk';
import { PersonalInfoReviewSection } from '@navio/nsdk';
import { isCarOnTireHotel } from './isCarOnTireHotel';

/** The shape of a paginated list as far as this flow cares about it. */
type Page<T> = { count: number; results: T[] };

/**
 * Why a section the brand asked for did or did not become a dialog.
 *
 * `nothingToAsk` and `cannotAsk` both mean no dialog, but they end differently:
 * having nothing to ask is a legitimate complete answer, while a list we could
 * not load in full leaves the review unfinished.
 */
type SectionOutcome = 'ask' | 'nothingToAsk' | 'cannotAsk';

export type ReviewPlan = {
  /** The dialogs this customer is actually shown, in the brand's order. */
  steps: PersonalInfoReviewSection[];
  /**
   * False when a section had to be dropped because it could not be shown in
   * full. Answering the remaining dialogs is then not a complete review, so it
   * must not be stamped — the prompt has to come back.
   */
  isComplete: boolean;
};

/** We only ever hold the first page, so more rows than that means we cannot ask. */
const isTruncated = <T>(page: Page<T>): boolean => page.count > page.results.length;

const resolveSection = ({
  section,
  cars,
  userGroupAddresses
}: {
  section: PersonalInfoReviewSection;
  cars: Page<CarFleetViewRecord> | undefined;
  userGroupAddresses: Page<UserGroupAddressRecord> | undefined;
}): SectionOutcome => {
  if (section === PersonalInfoReviewSection.CONTACT) {
    return 'ask';
  }

  if (section === PersonalInfoReviewSection.CARS) {
    if (!cars || isTruncated(cars)) {
      return 'cannotAsk';
    }
    // Every car is listed when at least one can go; the tire hotel badge
    // explains the rows that cannot. With none removable there is nothing to
    // ask, which still counts as having reviewed them.
    return cars.results.some((car) => !isCarOnTireHotel(car)) ? 'ask' : 'nothingToAsk';
  }

  if (!userGroupAddresses || isTruncated(userGroupAddresses)) {
    return 'cannotAsk';
  }
  // An empty list is still worth showing: the dialog offers Add an address.
  return 'ask';
};

/**
 * Decide, from the values we loaded, which dialogs the customer gets and whether
 * answering them all amounts to a full review.
 *
 * Confirming a list we only hold the first page of would record cars or
 * addresses the customer never saw as reviewed, so those sections stand down and
 * the review stays incomplete.
 */
export const buildReviewPlan = ({
  sections,
  cars,
  userGroupAddresses
}: {
  sections: PersonalInfoReviewSection[];
  cars: Page<CarFleetViewRecord> | undefined;
  userGroupAddresses: Page<UserGroupAddressRecord> | undefined;
}): ReviewPlan => {
  const resolved = sections.map((section) => ({
    section,
    outcome: resolveSection({ section, cars, userGroupAddresses })
  }));

  return {
    steps: resolved.filter(({ outcome }) => outcome === 'ask').map(({ section }) => section),
    isComplete: resolved.every(({ outcome }) => outcome !== 'cannotAsk')
  };
};
